import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import QRCodeStyling from "qr-code-styling";
import useAdminDataStore, { selectCurrentGroupData } from "../../store/adminData/adminData";
import useThemeStore, { selectTheme } from "../../store/theme/theme";
import { processLogoForQR } from "../../utils/circleCropLogo";

const DOWNLOAD_SIZE = 1024;

/**
 * GroupQR — single source of truth for QR code design across the app.
 *
 * Props:
 *   url          {string}  — URL to encode
 *   size         {number}  — display size in px (default 240)
 *   downloadName {string}  — filename (without extension) used on download
 *
 * Ref handle:
 *   download()   — triggers a high-res (1024px) PNG download
 */
const GroupQR = forwardRef(function GroupQR(
  { url, size = 240, downloadName = "qr-code" },
  ref,
) {
  const containerRef = useRef(null);
  const qrInstanceRef = useRef(null);

  const themeColors = useThemeStore(selectTheme);
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const groupLogo = groupData?.configuration?.logo;

  const buildOptions = useCallback(
    async (targetSize) => {
      let processedLogo = null;
      try {
        processedLogo = await processLogoForQR(groupLogo);
      } catch {
        // generate QR without logo on failure
      }

      const options = {
        type: "canvas",
        width: targetSize,
        height: targetSize,
        margin: 10,
        data: url,
        dotsOptions: { type: "rounded", color: "#1a1a1a" },
        cornersSquareOptions: {
          type: "extra-rounded",
          color: themeColors["primary-background"],
        },
        cornersDotOptions: {
          type: "dot",
          color: themeColors["primary-background"],
        },
        backgroundOptions: { color: "#ffffff" },
        qrOptions: { errorCorrectionLevel: "H" },
      };

      if (processedLogo) {
        options.image = processedLogo;
        options.imageOptions = {
          // hideBackgroundDots: false lets QR dots render behind the image —
          // the white circle baked into the PNG visually masks them, making
          // the cleared area look circular rather than the square the library
          // would produce with true.
          hideBackgroundDots: true,
          imageSize: 0.4,
          margin: 6,
          crossOrigin: "anonymous",
        };
      }

      return options;
    },
    [url, groupLogo, themeColors],
  );

  useEffect(() => {
    if (!url || !containerRef.current) return;

    let cancelled = false;

    buildOptions(size).then((options) => {
      if (cancelled || !containerRef.current) return;
      containerRef.current.innerHTML = "";
      qrInstanceRef.current = new QRCodeStyling(options);
      qrInstanceRef.current.append(containerRef.current);
    });

    return () => {
      cancelled = true;
    };
  }, [url, size, buildOptions]);

  const download = useCallback(async () => {
    if (!url) return;
    const options = await buildOptions(DOWNLOAD_SIZE);
    const hiResQR = new QRCodeStyling(options);
    hiResQR.download({ name: downloadName, extension: "png" });
  }, [url, downloadName, buildOptions]);

  useImperativeHandle(ref, () => ({ download }), [download]);

  return <div ref={containerRef} style={{ width: size, height: size, flexShrink: 0 }} />;
});

export default GroupQR;
